<script>
  import { onMount, onDestroy } from 'svelte';
  import { appSettings } from '../../store.js';
  import { getRecordingLoadProfile } from '../../recording-utils.js';
  import { isRecording, isPaused, cameraOk, cameraErr, cameraInterruption, recElapsed, actualDrawFps, recordingPerformanceWarning, recordingSaveState } from '../store.js';
  import { toggleRecord, togglePauseRecording, stopRecording, setMicGain, mediaRecorder, confirmRecordingSaved, retryRecordingSave } from '../../media/recording-service.js';
  import { camZoomCap, currentZoom, setCamZoom, camStream, getCameraElements, listVideoInputs, switchCamera } from '../../media/camera-service.js';


  let localZoom = 1;
  let micGain = 1;
  let resBadgeText = "";
  let resBadgeFullText = "";
  let resBadgeTone = "good";
  let videoDevices = [];
  let selectedDeviceId = appSettings.cameraDeviceId || "";

  let syncInterval;

  async function refreshDevices() {
    videoDevices = await listVideoInputs();
  }

  async function onCameraPick(e) {
    selectedDeviceId = e.target.value;
    await switchCamera(selectedDeviceId);
    await refreshDevices();
  }

  function syncBadge() {
    localZoom = currentZoom;
    
    // Calculate resolution badge text
    try {
      const { videoEl: liveV, canvasEl: outputCanvas } = getCameraElements();
      if (liveV && liveV.videoWidth) {
        const outputW = outputCanvas?.width || liveV.videoWidth;
        const outputH = outputCanvas?.height || liveV.videoHeight;
        
        let fullText = `${outputW}×${outputH}`;
        let shortText = `⚙️ ${Math.min(outputW, outputH)}p`;
        
        if (camStream) {
          const track = camStream.getVideoTracks()[0];
          if (track && track.getSettings) {
            const tr = track.getSettings();
            const fps = tr.frameRate ? Math.round(tr.frameRate) : (appSettings.fps || 30);
            fullText += ` · ${fps}fps`;
            shortText += ` · ${fps}fps`;
          }
        }
        
        const brMbps = Math.round((Number(appSettings.videoBitrate) || 12000000) / 1000000);
        fullText += ` · ${brMbps}Mbps`;
        
        if (mediaRecorder && mediaRecorder.mimeType) {
          const codecShort = mediaRecorder.mimeType.replace(/^video\//, "").replace(/;codecs=/, " ").toUpperCase();
          if (codecShort) fullText += ` · ${codecShort}`;
        }
        if ($actualDrawFps) fullText += ` · ${$actualDrawFps}fps render`;
        if ($recordingPerformanceWarning) fullText += " · LOW FPS";
        const loadProfile = getRecordingLoadProfile(appSettings);
        fullText += ` · ${loadProfile.label}`;
        resBadgeTone = loadProfile.tone;
        
        resBadgeFullText = fullText;
        resBadgeText = shortText;
      }
    } catch (e) {
      // Ignore
    }
  }

  onMount(() => {
    micGain = appSettings.micGain || 1;
    syncBadge();
    refreshDevices();
    syncInterval = setInterval(syncBadge, 500); // 2fps UI sync for badge
  });

  // Labels only populate once the camera stream (and thus permission) is live —
  // refresh the list the moment the camera comes up.
  $: if ($cameraOk) refreshDevices();

  onDestroy(() => {
    if (syncInterval) clearInterval(syncInterval);
  });

  function handleZoom(z) {
    if (!camStream) return;
    const track = camStream.getVideoTracks()[0];
    if (camZoomCap && track && track.applyConstraints) {
      const actualZoom = Math.min(Math.max(camZoomCap.min, z), camZoomCap.max);
      track.applyConstraints({ advanced: [{ zoom: actualZoom }] }).then(() => {
        setCamZoom(z);
        syncBadge();
      }).catch(e => console.warn("Zoom error", e));
    }
  }

  function handleMicGain(e) {
    const v = Number(e.target.value) / 100;
    micGain = v;
    setMicGain(v);
  }
</script>

<div class="camera-controls">
  {#if $cameraInterruption.status !== 'live'}
    <div class="cam-err-msg" role="alert">
      ⚠️ {$cameraInterruption.message || 'Camera video interrupted'}
    </div>
  {/if}
  {#if $cameraErr}
    <div class="cam-err-msg">
      ⚠️ {$cameraErr}
    </div>
  {:else if !$cameraOk}
    <div class="cam-loading">
      Starting camera...
    </div>
  {:else}
    <!-- Top Bar: Timer and Resolution -->
    <div class="top-bar">
      <div class="live-indicator">
        <div class="dot" class:recording={$isRecording} class:paused={$isPaused}></div>
        <span class="text">{$isRecording ? ($isPaused ? 'PAUSED' : 'REC') : 'STANDBY'}</span>
        <span class="timer" id="rec-timer">{$recElapsed || "00:00"}</span>
      </div>
      
      <div class="top-right-group">
        {#if resBadgeText}
          <div class="res-badge tone-{resBadgeTone}" title={resBadgeFullText}>{resBadgeText}</div>
        {/if}

        <!-- Side Controls: Camera Picker, Zoom, Mic -->
        <div class="side-controls">
          <!-- Camera Source Picker — only when there's a real choice and not mid-record -->
          {#if videoDevices.length > 1 && !$isRecording}
            <div class="cam-source-group">
              <span class="icon">🎥</span>
              <select class="cam-source-select" value={selectedDeviceId} on:change={onCameraPick} aria-label="Camera source">
                <option value="">Auto (back camera)</option>
                {#each videoDevices as dev, i_each (i_each)}
                  <option value={dev.deviceId}>{dev.label}</option>
                {/each}
              </select>
            </div>
          {/if}

          <!-- Zoom Controls -->
          {#if camZoomCap}
            <div class="zoom-group">
              {#each [1, 2, 3] as z, i_each (i_each)}
                <button 
                  class="btn-zoom" 
                  class:active={localZoom === z}
                  on:click={() => handleZoom(z)}
                >
                  {z}x
                </button>
              {/each}
            </div>
          {/if}

          <!-- Mic Gain Control -->
          <div class="mic-group">
            <span class="icon">🎤</span>
            <input 
              type="range" 
              min="0" 
              max="1000" 
              step="10" 
              value={Math.round(micGain * 100)} 
              on:input={handleMicGain}
            />
            <span class="mic-val">{micGain.toFixed(1)}x</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Bar: Record actions -->
    <div class="bottom-bar">
      
      <div class="rec-actions">
        {#if !$isRecording}
          <button class="btn-rec start" aria-label="Start recording" on:click={toggleRecord}>
            <div class="inner-circle"></div>
          </button>
        {:else}
          <button class="btn-rec pause" aria-label={$isPaused ? 'Resume recording' : 'Pause recording'} on:click={togglePauseRecording}>
            {$isPaused ? '▶' : '⏸'}
          </button>
          <button class="btn-rec stop" aria-label="Stop and save recording" on:click={stopRecording}>
            <div class="inner-square"></div>
          </button>
        {/if}
      </div>

    </div>

    {#if $recordingSaveState.status !== 'idle' && $recordingSaveState.status !== 'confirmed'}
      <div class="recording-save-notice save-{$recordingSaveState.status}" aria-live="polite">
        <div>
          <strong>{$recordingSaveState.status === 'finalizing' ? 'Finalizing recording…' : $recordingSaveState.message}</strong>
          {#if $recordingSaveState.fileName}<span>{$recordingSaveState.fileName}</span>{/if}
        </div>
        {#if $recordingSaveState.status === 'download-started'}
          <button on:click={confirmRecordingSaved}>I Can See The File</button>
        {:else if $recordingSaveState.status === 'error'}
          <button on:click={retryRecordingSave}>Retry Save</button>
        {/if}
      </div>
    {/if}
  {/if}
</div>

<style>
  .camera-controls {
    position: absolute;
    inset: 0;
    pointer-events: none; /* Let clicks pass through to canvas if needed */
    display: flex;
    flex-direction: column;
    padding: 16px;
    z-index: 10;
  }

  .cam-loading, .cam-err-msg {
    align-self: center;
    margin-top: auto;
    margin-bottom: auto;
    background: rgba(0,0,0,0.7);
    padding: 12px 24px;
    border-radius: 8px;
    color: white;
    font-weight: 600;
  }
  .cam-err-msg { color: #fca5a5; border: 1px solid #ef4444; }

  /* Top Bar */
  .top-bar {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    pointer-events: auto;
    margin-bottom: 12px;
  }
  .top-right-group {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  }

  .live-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(0,0,0,0.6);
    backdrop-filter: blur(4px);
    padding: 6px 12px;
    border-radius: 20px;
    border: 1px solid rgba(255,255,255,0.1);
  }
  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #9ca3af;
  }
  .dot.recording {
    background: #ef4444;
    animation: pulse 1.5s infinite alternate;
  }
  .dot.paused {
    background: #f59e0b;
    animation: none;
  }
  @keyframes pulse { from { opacity: 0.4; } to { opacity: 1; } }
  
  .text {
    font-size: 12px;
    font-weight: 800;
    color: #fff;
    letter-spacing: 1px;
  }
  .timer {
    font-size: 14px;
    font-family: monospace;
    font-weight: 600;
    color: #d1d5db;
    margin-left: 4px;
  }

  .res-badge {
    background: rgba(0,0,0,0.6);
    backdrop-filter: blur(4px);
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 10px;
    color: #9ca3af;
    font-family: monospace;
    border: 1px solid rgba(255,255,255,0.1);
    white-space: nowrap;
  }
  .res-badge.tone-good {
    color: #bbf7d0;
    border-color: rgba(134,239,172,0.35);
  }
  .res-badge.tone-warn {
    color: #fde68a;
    border-color: rgba(251,191,36,0.4);
  }
  .res-badge.tone-danger {
    color: #fecaca;
    border-color: rgba(248,113,113,0.5);
  }

  /* Bottom Bar */
  .bottom-bar {
    margin-top: auto;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    pointer-events: auto;
    padding-bottom: 8px;
  }

  .side-controls {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    pointer-events: auto;
    background: rgba(15, 23, 42, 0.75);
    backdrop-filter: blur(8px);
    border-radius: 12px;
    padding: 6px 10px;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .zoom-group {
    display: flex;
    background: rgba(0,0,0,0.6);
    border-radius: 8px;
    overflow: hidden;
    width: fit-content;
  }
  .btn-zoom {
    background: transparent;
    color: #fff;
    border: none;
    padding: 4px 10px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
  }
  .btn-zoom.active {
    background: #2563eb;
  }

  .cam-source-group {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(0,0,0,0.6);
    padding: 4px 8px;
    border-radius: 8px;
    width: fit-content;
    max-width: 180px;
  }
  .cam-source-group .icon { font-size: 12px; }
  .cam-source-select {
    background: transparent;
    color: #fff;
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 6px;
    padding: 2px 4px;
    font-size: 11px;
    font-weight: 600;
    max-width: 140px;
  }
  .cam-source-select option { color: #111; }

  .mic-group {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(0,0,0,0.6);
    padding: 4px 8px;
    border-radius: 8px;
  }
  .mic-group .icon { font-size: 12px; }
  .mic-group input[type="range"] {
    width: 60px;
    accent-color: #10b981;
  }
  .mic-val {
    font-size: 11px;
    color: #fff;
    font-family: monospace;
    min-width: 32px;
    text-align: right;
  }

  .rec-actions {
    display: flex;
    gap: 16px;
    align-items: center;
    flex-shrink: 0;
  }

  /* VERTICAL (portrait) recording adjustments */
  @media (orientation: portrait) {
    .bottom-bar {
      align-items: center;
      justify-content: center;
    }
    .side-controls {
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 6px;
      max-width: 100%;
      padding: 6px;
    }
    .mic-group input[type="range"] { width: 64px; }
    .top-bar {
      flex-wrap: wrap;
      gap: 6px;
    }
    .live-indicator { padding: 3px 8px; font-size: 11px; }
    .timer { font-size: 11px; }
    .btn-rec.start { width: 52px; height: 52px; }
    .btn-rec.start .inner-circle { width: 38px; height: 38px; }
  }

  .recording-save-notice {
    pointer-events: auto; align-self: center; display: flex; align-items: center; gap: 12px;
    max-width: min(560px, 92%); padding: 10px 12px; border-radius: 12px;
    color: #fff; background: rgba(15,23,42,.94); border: 1px solid #475569;
  }
  .recording-save-notice > div { min-width: 0; }
  .recording-save-notice strong, .recording-save-notice span { display: block; }
  .recording-save-notice span { margin-top: 2px; color: #cbd5e1; font-size: 9px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .recording-save-notice button { flex: none; padding: 7px 10px; border: 0; border-radius: 8px; background: #10b981; color: #fff; font-weight: 800; }
  .recording-save-notice.save-error { border-color: #ef4444; }

  .btn-rec {
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    transition: transform 0.1s;
  }
  .btn-rec:active { transform: scale(0.95); }
  
  .btn-rec.start {
    width: 56px;
    height: 56px;
    background: rgba(255,255,255,0.2);
    border: 3px solid #fff;
  }
  .btn-rec.start .inner-circle {
    width: 40px;
    height: 40px;
    background: #ef4444;
    border-radius: 50%;
  }

  .btn-rec.pause {
    width: 44px;
    height: 44px;
    background: #f59e0b;
    color: #fff;
    font-size: 18px;
  }

  .btn-rec.stop {
    width: 44px;
    height: 44px;
    background: rgba(255,255,255,0.2);
    border: 3px solid #fff;
  }
  .btn-rec.stop .inner-square {
    width: 18px;
    height: 18px;
    background: #ef4444;
    border-radius: 4px;
  }
</style>
